// src/pages/BulkReview.tsx
import React, { useEffect, useState, useCallback, Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { getApiBaseUrl, getReportApiBaseUrl } from '../config/api';
import { fetchWithAuth } from '../utils/auth';
import { suspendAccount, restoreAccount } from '../utils/accountActions';
import { ACCOUNT_REASONS, AccountReasonCode, DEFAULT_BULK_REASON, accountActionError, reasonLabel, refusalText, REASON_SHOWN_NOTE } from './live-review/account-reasons';

interface VideoItem {
  id: string;
  url?: string;
  [key: string]: any;
}

const PAGE_SIZE = 100;
const STATUSES = ['active', 'failed', 'uploaded', 'deleted', 'blocked', 'muted', 'banned', 'denied', 'review', 'under_review', 'review_required', 'review_failed', 'processing'];

// Account statuses for the USER endpoint
type UserAccountStatus = 'active' | 'inactive' | 'suspended' | 'deleted' | 'blocked';
const USER_ACCOUNT_STATUSES: UserAccountStatus[] = ['active', 'inactive', 'suspended', 'deleted', 'blocked'];

const REASONS = [
  { code: 'ACTIVE', label: 'ACTIVE - Safe Video', value: 'ACTIVE - Safe Video: Video is safe' },
  { code: 'OTHERS', label: 'OTHERS - No Comment', value: 'OTHERS - No Comment: Other' },
  { code: 'PORN', label: 'PORN - Pornography / Sexual Services (Tier A)', value: 'PORN - Pornography or Sexual Services: Explicit sexual acts, exposed genitalia or sexual services' },
  { code: 'NSFW', label: 'NSFW - Nudity / Sexual Content', value: 'NSFW - Nudity / Sexual Content: Contains explicit or adult material' },
  { code: 'DUPLICATE', label: 'Duplicate - Duplicate Content', value: 'Duplicate - Duplicate Content: Content is a duplicate' },
  { code: 'VIOLENCE', label: 'VIOLENCE - Violent or Graphic Content', value: 'VIOLENCE - Violent or Graphic Content: Shows physical harm or violent behavior' },
  { code: 'TERROR', label: 'TERROR - Terrorist or Extremist Content', value: 'TERROR - Terrorist or Extremist Content: Promotes or glorifies terrorism' },
  { code: 'HATE', label: 'HATE - Hate Speech', value: 'HATE - Hate Speech: Contains racist, sexist, or discriminatory language' },
  { code: 'HARASS', label: 'HARASS - Bullying or Harassment', value: 'HARASS - Bullying or Harassment: Targets individuals with abusive content' },
  { code: 'DRUGS', label: 'DRUGS - Drugs or Substance Use', value: 'DRUGS - Drugs or Substance Use: Promotes or shows illegal drug use' },
  { code: 'SPAM', label: 'SPAM - Spam or Fake Engagement', value: 'SPAM - Spam or Fake Engagement: Misleading content, scams, or fake activity' },
  { code: 'COPYRIGHT', label: 'COPYRIGHT - Copyright Violation', value: 'COPYRIGHT - Copyright Violation: Uses copyrighted music or video without rights' },
  { code: 'MINOR', label: 'MINOR - Exploitation of Minors', value: 'MINOR - Exploitation of Minors: Shows or targets minors in unsafe ways' },
  { code: 'FAKE', label: 'FAKE - Misleading or Fake Content', value: 'FAKE - Misleading or Fake Content: Deliberately misleading or manipulated video' },
  { code: 'DANGEROUS', label: 'DANGEROUS - Dangerous Acts', value: 'DANGEROUS - Dangerous Acts: Encourages harmful challenges or unsafe behavior' },
  { code: 'SENSITIVE', label: 'SENSITIVE - Sensitive or Disturbing Content', value: 'SENSITIVE - Sensitive or Disturbing Content: May disturb viewers (e.g., blood, self-harm)' },
  { code: 'MALWARE', label: 'MALWARE - Malicious Links', value: 'MALWARE - Malicious Links: Links to harmful or phishing websites' },
  { code: 'UNVERIFIED_AD', label: 'UNVERIFIED_AD - Unapproved Advertising', value: 'UNVERIFIED_AD - Unapproved Advertising: Promotes products without approval' },
  { code: 'REPEAT_OFFENSE', label: 'REPEAT_OFFENSE - Repeat Policy Violation', value: 'REPEAT_OFFENSE - Repeat Policy Violation: Repeated uploads of banned content' },
];

/**
 * The removal vocabulary, grouped by consequence.
 *
 * ZERO codes close the account outright, so they are separated and confirmed rather
 * than sitting one pixel from NSFW in a flat grid.
 *
 * ACTIVE is deliberately absent: it is an APPROVAL, not a removal reason. It was
 * selectable as a block reason before, which is why 336 blocked and 4,351 deleted
 * videos currently read "ACTIVE - Safe Video: Video is safe" as their justification.
 */
const REASON_GROUPS: {
  title: string;
  note: string;
  codes: string[];
  labelClass: string;
  btnClass: string;
  confirm?: boolean;
}[] = [
  {
    title: 'Zero tolerance',
    note: 'closes the account (PORN: content posted from 22 Sep 2026; before that it is a Severe breach)',
    codes: ['MINOR', 'PORN', 'TERROR'],
    labelClass: 'text-red-700',
    btnClass: 'bg-red-600 border-red-700 text-white hover:bg-red-700',
    confirm: true,
  },
  {
    title: 'Severe',
    note: 'removes + records a violation',
    codes: ['NSFW', 'VIOLENCE', 'HATE', 'HARASS', 'DRUGS', 'DANGEROUS', 'SENSITIVE'],
    labelClass: 'text-amber-700',
    btnClass: 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100',
  },
  {
    title: 'Standard',
    note: 'removes + records a violation',
    codes: ['SPAM', 'FAKE', 'COPYRIGHT', 'MALWARE', 'UNVERIFIED_AD'],
    labelClass: 'text-slate-600',
    btnClass: 'bg-slate-50 border-slate-300 text-slate-700 hover:bg-slate-100',
  },
  {
    title: 'Housekeeping',
    note: 'removes, no violation recorded',
    codes: ['DUPLICATE', 'REPEAT_OFFENSE', 'OTHERS'],
    labelClass: 'text-gray-500',
    btnClass: 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100',
  },
];

const reasonValue = (code: string) => REASONS.find((r) => r.code === code)!.value;
const NSFW_REASON = reasonValue('NSFW');

/** The internal note kept with an account action made from here: the code the person is shown and the video status reason in use.
 *  At least ten characters. */
const bulkNote = (reason: string, code?: string) => {
  const note = `Bulk Review${code ? ` [${code}]` : ''}: ${reason.trim()}`;
  return note.length >= 10 ? note.slice(0, 2000) : 'Bulk Review: account action';
};
const VIOLENCE_REASON = reasonValue('VIOLENCE');
const DUPLICATE_REASON = reasonValue('DUPLICATE');
const MINOR_REASON = reasonValue('MINOR');

/**
 * True when the AI's own words put a minor in a sexual or exploitative context.
 *
 * Until 2026-09-19 nothing surfaced this: the one-click tiles are NSFW / Violence / Dup,
 * and a video whose AI flag read "distribution of pornography, specifically involving a
 * minor" was removed as VIOLENCE -- no child-safety code, no referral (0 referrals, ever).
 * Both halves are required so "suitable for children" and ordinary kids' content do not
 * light up.
 */
const MINOR_TERMS = /\b(minors?|child(ren)?|underage|under-age|kids?|teen(ager)?s?|juvenile|school ?(girl|boy)s?|\d{1,2}[- ]year[- ]old)\b/i;
const EXPLOIT_TERMS = /\b(porn\w*|sexual\w*|sexually|explicit|nud(e|ity)|exploit\w*|abuse\w*|groom\w*|csam|molest\w*)\b/i;
// The AI's audience boilerplate ("unsuitable for minors") and its negations ("no explicit
// content") are not findings about a child. Calibrated on production flags, 2026-09-19:
// without these, every suggestive or violent video lit up.
const AUDIENCE_BOILERPLATE =
  /\b(unsuitable|inappropriate|not suitable|not appropriate|harmful|suitable|safe)\s+for\s+(all\s+)?(minors|children|kids|young (viewers|audiences)|audiences)\b/gi;
const NEGATED =
  /\b(no|not|non|without|free of|never)\b(\W+\w+){0,3}?\W+(explicit|sexual\w*|nud(e|ity)|porn\w*)(\W+(or|and|,)\W*(explicit|sexual\w*|nud(e|ity)|porn\w*))*/gi;
export const childSafetySignal = (video: VideoItem): boolean => {
  const text = [video.VideoStatusReason, video.LlmFlag, video.LlmDescription, video.Keywords]
    .filter((t) => typeof t === 'string')
    .join(' ')
    .replace(AUDIENCE_BOILERPLATE, ' ')
    .replace(NEGATED, ' ');
  return MINOR_TERMS.test(text) && EXPLOIT_TERMS.test(text);
};

const BulkReview: React.FC = () => {
  const location = useLocation();
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [reviewedFilter, setReviewedFilter] = useState<'all' | 'true' | 'false'>('false');
  const [videoStatusFilter, setVideoStatusFilter] = useState<string>('');
  // Separate input state (what user types) from filter state (what triggers search)
  const [searchUserIdInput, setSearchUserIdInput] = useState<string>('');
  const [searchUserNameInput, setSearchUserNameInput] = useState<string>('');
  const [userIdFilter, setUserIdFilter] = useState<string>('');
  const [userNameFilter, setUserNameFilter] = useState<string>('');

  // Modal state
  const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [reasonInput, setReasonInput] = useState<string>('');

  // Video progress tracking for grid thumbnails
  const [videoProgress, setVideoProgress] = useState<Record<string, number>>({});
  const [actionLoading, setActionLoading] = useState(false);

  // Background review tracking
  const [reviewedInBackground, setReviewedInBackground] = useState<Set<string>>(new Set());
  const [pendingReviews, setPendingReviews] = useState<Set<string>>(new Set());
  const [failedReviews, setFailedReviews] = useState<Set<string>>(new Set());

  // Refs for tracking pending reviews (to avoid stale state in async loops)
  const pendingReviewsRef = React.useRef<Set<string>>(new Set());
  // Only the newest list load and the newest background review run may act: an older response must not replace a
  // newer page, and an older loop must not keep marking the previous page reviewed.
  const loadSeqRef = React.useRef(0);
  const reviewRunRef = React.useRef(0);
  // Leaving the page ends the run too, or it keeps marking videos reviewed that nobody is looking at.
  useEffect(
    () => () => {
      reviewRunRef.current++;
    },
    []
  );

  // User status modal
  const [userStatusModalOpen, setUserStatusModalOpen] = useState(false);
  const [userStatus, setUserStatus] = useState<UserAccountStatus | ''>('');
  const [userIdInput, setUserIdInput] = useState<string>('');
  // The reason the person is shown when suspended (their own account screen); default is the common case in this queue.
  // The modal has its own reason. Tiles keep theirs per person (by user id) so one person's reason is never used for another.
  const [reasonCode, setReasonCode] = useState<AccountReasonCode>(DEFAULT_BULK_REASON);
  const [tileReasons, setTileReasons] = useState<Record<string, AccountReasonCode>>({});
  const tileReason = (userId: string): AccountReasonCode => tileReasons[userId] ?? DEFAULT_BULK_REASON;
  const clearTileReason = (userId: string) =>
    setTileReasons((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  const [userStatusLoading, setUserStatusLoading] = useState(false);

  // Action feedback indicator - track which video and what action
  const [lastAction, setLastAction] = useState<{ videoId: string; action: string; timestamp: number } | null>(null);

  // Track suspended users
  const [suspendedUsers, setSuspendedUsers] = useState<Set<string>>(new Set());

  // Track when no more videos available
  const [allVideosReviewed, setAllVideosReviewed] = useState(false);

  // Track blocked videos in this session
  const [blockedInSession, setBlockedInSession] = useState<Set<string>>(new Set());

  // Approve all loading state
  const [approveAllLoading, setApproveAllLoading] = useState(false);
  const [approveAllProgress, setApproveAllProgress] = useState<{ current: number; total: number; skipped: number } | null>(null);

  // Auto-review toggle - enabled by default
  const [autoReviewEnabled, setAutoReviewEnabled] = useState(true);
  const autoReviewEnabledRef = React.useRef(true);

  // Update ref when state changes
  React.useEffect(() => {
    autoReviewEnabledRef.current = autoReviewEnabled;

    // If auto-review is turned OFF, clear all pending reviews immediately
    if (!autoReviewEnabled) {
      setPendingReviews(new Set());
      toast.info('Auto-review stopped. Pending reviews cancelled.');
    }
  }, [autoReviewEnabled]);

  // Auto-clear action indicator after 3 seconds
  React.useEffect(() => {
    if (lastAction) {
      const timer = setTimeout(() => {
        setLastAction(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [lastAction]);

  // Background review: Mark videos as reviewed in the background
  const startBackgroundReview = useCallback(async (videoList: VideoItem[], _token: string) => {
    const run = ++reviewRunRef.current;
    // Process videos one by one in the background
    for (const video of videoList) {
      // CHECK: Stop if auto-review is disabled
      if (!autoReviewEnabledRef.current) {
        console.log('Auto-review disabled, stopping background review');
        break;
      }
      if (run !== reviewRunRef.current) break;

      // Skip if already reviewed
      if (video.Reviewed === true) {
        setReviewedInBackground((prev) => new Set(prev).add(video.id));
        continue;
      }

      // Mark as pending
      pendingReviewsRef.current.add(video.id);
      setPendingReviews((prev) => new Set(prev).add(video.id));

      let success = false;
      let retries = 0;
      const maxRetries = 3;

      // Retry logic for failed requests
      while (!success && retries < maxRetries && autoReviewEnabledRef.current && run === reviewRunRef.current) {
        // CHECK: Stop if auto-review is disabled
        if (!autoReviewEnabledRef.current) {
          console.log('Auto-review disabled during retry, stopping');
          break;
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000);
        try {
          // fetchWithAuth reads the latest token on every call and refreshes + retries once.
          const res = await fetchWithAuth(`${getApiBaseUrl()}/video/t/updateVideo`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ItemID: video.id,
            }),
            signal: controller.signal,
          });
          clearTimeout(timeoutId);

          if (res.ok) {
            // Mark as reviewed in background
            setReviewedInBackground((prev) => new Set(prev).add(video.id));
            success = true;
          } else {
            retries++;
            if (retries < maxRetries) {
              // Short backoff — was 1s/2s, now 300ms/600ms to keep things snappy
              const delay = 300 * retries;
              await new Promise((resolve) => setTimeout(resolve, delay));
            }
          }
        } catch (error) {
          clearTimeout(timeoutId);
          // Silent error - just log to console
          if ((error as any)?.name === "AbortError") { console.warn(`Background review timeout attempt ${retries + 1} for video ${video.id}`); } else { console.error(`Background review attempt ${retries + 1} failed for video ${video.id}:`, error); }
          retries++;
          if (retries < maxRetries) {
            const delay = 300 * retries;
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }

      // Remove from pending
      pendingReviewsRef.current.delete(video.id);
      setPendingReviews((prev) => {
        const newSet = new Set(prev);
        newSet.delete(video.id);
        return newSet;
      });

      // Track failed reviews
      if (!success && run === reviewRunRef.current) {
        setFailedReviews((prev) => new Set(prev).add(video.id));
      }

      // Small delay between requests
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }, []);

  const loadVideos = useCallback(async (newPage: number) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found');
      return;
    }

    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const isUserSearch = userIdFilter.trim() || userNameFilter.trim();

      const body: any = { From: newPage, BatchSize: PAGE_SIZE };
      if (reviewedFilter !== 'all') body.Reviewed = reviewedFilter === 'true';
      if (videoStatusFilter) body.VideoStatus = videoStatusFilter;
      if (userIdFilter.trim()) body.UserID = userIdFilter.trim();
      if (userNameFilter.trim()) body.UserName = userNameFilter.trim();

      // All video fetches route through the report-api Redis search endpoint.
      // That endpoint handles both modes itself:
      //   - user-specific search (UserID / UserName) → Redis creator set
      //     + Mongo `videos` fallback for blocked/deleted entries
      //   - status-only browse → straight Mongo `videos` query
      // Bypasses the cross-origin api.tezda.tech path entirely (which was
      // hitting CORS from tezda-report.pages.dev).
      const res = await fetchWithAuth(`${getReportApiBaseUrl()}/redis-search/videos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const json: any = await res.json();
      if (seq !== loadSeqRef.current) return;
      if (!res.ok) throw new Error(json.message || 'Failed to load');

      // Prefer the 480p rendition for review — large enough to make a call
      // on content without burning bandwidth at 1080p (480p is ~6-8x smaller).
      // If 480p isn't available, walk to the nearest smaller, then larger,
      // then anything. Fall back to VideoUrl only when there are no
      // resolutions at all.
      const PREFERRED_RES = ['480p', '360p', '720p', '1080p'];
      const pickReviewUrl = (v: any): string | undefined => {
        const list = Array.isArray(v?.VideoResolutions)
          ? v.VideoResolutions.filter((r: any) => typeof r === 'string' && r.trim())
          : [];
        for (const target of PREFERRED_RES) {
          const match = list.find((r: string) => r.includes(target));
          if (match) return match;
        }
        return list[0] || v?.VideoUrl;
      };

      const items: VideoItem[] = (json.data.videosResponse || [])
        .map((v: any) => ({
          ...v,
          id: v._id,
          url: pickReviewUrl(v),
        }))
        .filter((v: any) => v.id);

      // Check if no more videos available
      if (items.length === 0) {
        setAllVideosReviewed(true);
      } else {
        setAllVideosReviewed(false);
      }

      setVideos(items);
      setPage(newPage);

      // Reset background tracking for new page
      setReviewedInBackground(new Set());
      pendingReviewsRef.current = new Set();
      setPendingReviews(new Set());
      setFailedReviews(new Set());

      // Start background review process for all videos on this page
      // Only if enabled AND not a user-specific search (user searches are for manual review)
      if (autoReviewEnabled && !isUserSearch) {
        startBackgroundReview(items, token);
      }
    } catch (e: any) {
      if (seq === loadSeqRef.current) toast.error(e.message);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [reviewedFilter, videoStatusFilter, userIdFilter, userNameFilter, startBackgroundReview, autoReviewEnabled]);

  // A status comes with its default reviewedFilter, set in the SAME update so the list loads once:
  // "active" -> Unreviewed, all others -> All Videos. Setting it from an effect after the status changed loaded the
  // list twice and started two background review loops.
  const chooseVideoStatus = useCallback((status: string) => {
    setVideoStatusFilter(status);
    if (status === 'active') {
      setReviewedFilter('false'); // Unreviewed
    } else if (status) {
      setReviewedFilter('all'); // All Videos
    }
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const queryUserId = query.get('userId')?.trim() || '';
    const queryUserName = query.get('userName')?.trim() || '';

    if (!queryUserId && !queryUserName) {
      return;
    }

    setSearchUserIdInput(queryUserId);
    setSearchUserNameInput(queryUserName);
    setUserIdFilter(queryUserId);
    setUserNameFilter(queryUserName);
    setReviewedFilter('all');
    setVideoStatusFilter('');
  }, [location.search]);

  // Handle search button click - applies input values to filters
  const handleSearch = useCallback(() => {
    const userId = searchUserIdInput.trim();
    const userName = searchUserNameInput.trim();

    if (userId || userName) {
      setUserIdFilter(userId);
      setUserNameFilter(userName);
      setReviewedFilter('all');
      setVideoStatusFilter('');
    }
  }, [searchUserIdInput, searchUserNameInput]);

  // Clear search filters
  const handleClearSearch = useCallback(() => {
    setSearchUserIdInput('');
    setSearchUserNameInput('');
    setUserIdFilter('');
    setUserNameFilter('');
  }, []);

  useEffect(() => {
    // Load videos if a video status filter is selected OR if a user ID/name filter is applied
    if (videoStatusFilter || userIdFilter.trim() || userNameFilter.trim()) {
      loadVideos(1);
    }
  }, [loadVideos, videoStatusFilter, reviewedFilter, userIdFilter, userNameFilter]);

  // ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isModalOpen) {
          setIsModalOpen(false);
          setSelectedVideo(null);
        }
        if (userStatusModalOpen) {
          setUserStatusModalOpen(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModalOpen, userStatusModalOpen]);

  // Mark all current page videos as reviewed and move to next page
  const markAllReviewedAndNext = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found');
      return;
    }

    setLoading(true);
    try {
      // Count videos that actually need reviewing (not already reviewed)
      const videosNeedingReview = videos.filter((v) => v.Reviewed !== true);
      const alreadyReviewedCount = videos.length - videosNeedingReview.length;

      // Use ref for accurate pending count in async context
      const stillPending = pendingReviewsRef.current.size;

      // Show status message
      if (stillPending > 0) {
        toast.info(`Waiting for ${stillPending} videos to finish processing...`);

        // Wait for all pending reviews to complete (max 30 seconds)
        const maxWaitTime = 30000; // 30 seconds
        const startTime = Date.now();

        // Use ref instead of state for accurate tracking in async loop
        while (pendingReviewsRef.current.size > 0 && (Date.now() - startTime) < maxWaitTime) {
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Update progress every 2 seconds
          const elapsed = Date.now() - startTime;
          if (elapsed % 2000 < 500 && pendingReviewsRef.current.size > 0) {
            toast.info(`Still waiting: ${pendingReviewsRef.current.size} videos remaining...`, {
              autoClose: 1000,
            });
          }
        }

        const finalPending = pendingReviewsRef.current.size;
        if (finalPending > 0) {
          toast.warning(`Timeout: ${finalPending} videos still pending. Loading fresh videos anyway.`);
        } else if (failedReviews.size > 0) {
          toast.warning(`${reviewedInBackground.size} reviewed, ${failedReviews.size} failed, ${alreadyReviewedCount} already reviewed`);
        } else {
          toast.success(`All ${videos.length} videos processed! (${alreadyReviewedCount} were already reviewed)`);
        }
      } else {
        // No pending reviews - either auto-review is off or all videos were already reviewed
        if (alreadyReviewedCount === videos.length) {
          toast.success(`All ${videos.length} videos were already reviewed. Loading fresh videos...`);
        } else if (failedReviews.size > 0) {
          toast.warning(`${reviewedInBackground.size} reviewed, ${failedReviews.size} failed, ${alreadyReviewedCount} already reviewed`);
        } else if (!autoReviewEnabled) {
          toast.info('Auto-review is off. Loading fresh videos...');
        } else {
          toast.success(`All ${videos.length} videos marked as reviewed!`);
        }
      }

      // Reload from page 1 to get fresh unreviewed videos
      // (reviewed videos are filtered out by the API, so page 1 always shows new content)
      await loadVideos(1);
    } catch (e: any) {
      console.error('Error loading fresh videos:', e);
      toast.error(`Failed to load videos: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const openVideoModal = (video: VideoItem) => {
    setSelectedVideo(video);
    setIsModalOpen(true);
    setSelectedAction('');
    setReasonInput('');
  };

  const openUserStatusModal = () => {
    const defaultId = selectedVideo ? (selectedVideo.UserID || selectedVideo.UserId || '') : '';
    setUserIdInput(defaultId);
    setUserStatus('');
    setUserStatusModalOpen(true);
  };

  const confirmAction = async (action?: string, reason?: string) => {
    if (!selectedVideo) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('No token found');

      const actionToUse = (action || selectedAction).toLowerCase();

      const res = await fetchWithAuth(`${getApiBaseUrl()}/video/t/updateVideo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ItemID: selectedVideo.id,
          Action: actionToUse,
          VideoStatusReason: reason || reasonInput,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(refusalText(res.status, j.message, 'Failed to update video'));

      const actionTaken = actionToUse.toUpperCase();
      toast.success(`Video ${actionTaken}`);
      setLastAction({ videoId: selectedVideo.id, action: actionTaken, timestamp: Date.now() });

      // Update video status in local state instead of reloading
      setVideos((prevVideos) =>
        prevVideos.map((v) =>
          v.id === selectedVideo.id
            ? { ...v, VideoStatus: actionToUse }
            : v
        )
      );

      // Delay closing modal so user can see the action indicator
      setTimeout(() => {
        setIsModalOpen(false);
        setSelectedVideo(null);
      }, 800);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
      setSelectedAction('');
    }
  };

  const updateUserAccountStatus = async () => {
    const targetId = userIdInput.trim();
    if (!targetId || !userStatus) return;
    setUserStatusLoading(true);
    try {
      const done = userStatus;
      if (userStatus === 'suspended') {
        // Gated route: moderators only, the reason the person is shown, sessions cut off at once.
        // A blocked/inactive account answers 409: that message is shown and nothing changes.
        const out = await suspendAccount(targetId, reasonCode, bulkNote(`status set to suspended`, reasonCode));
        if (out.already) toast.info(out.message);
        else if (out.revocation) (out.revocation.warn ? toast.warn : toast.info)(out.revocation.text);
      } else if (userStatus === 'active') {
        const out = await restoreAccount(targetId, bulkNote('status set to active'));
        if (out.via === 'legacy' && out.message) toast.info(out.message);
      } else {
        const token = localStorage.getItem('authToken');
        if (!token) throw new Error('No token found');

        const res = await fetchWithAuth(`${getApiBaseUrl()}/user/t/updateUserAccountStatus`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ UserID: targetId, AccountStatus: userStatus }),
        });

        const j = await res.json();
        if (!res.ok) throw new Error(refusalText(res.status, j?.message, 'Failed to update account status'));
      }

      toast.success(`User status set to ${done}`);

      // Track suspended/blocked users
      if (userStatus === 'suspended' || userStatus === 'blocked') {
        setSuspendedUsers((prev) => new Set(prev).add(targetId));
      } else if (userStatus === 'active') {
        setSuspendedUsers((prev) => {
          const next = new Set(prev);
          next.delete(targetId);
          return next;
        });
      }

      setUserStatusModalOpen(false);
    } catch (err: any) {
      toast.error(accountActionError(err));
    } finally {
      setUserStatusLoading(false);
    }
  };

  const getVideoThumbnail = (video: VideoItem) => {
    if (video.VideoThumbnail) return video.VideoThumbnail;
    if (!video.url) return '';
    return `${video.UploadedFromR2 === true ? 'https://cdn2.pingtop.co' : 'https://cdn.pingtop.co'}${video.url}`;
  };

  // Quick action: Block video with a one-click reason.
  // `reason` is passed explicitly by the NSFW / Violence buttons; when omitted we
  // fall back to the filter-derived default (review_required = NSFW, else VIOLENCE).
  const quickBlockVideo = async (video: VideoItem, reason?: string) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found');
      return;
    }

    const defaultReason =
      reason ?? (videoStatusFilter === 'review_required' ? NSFW_REASON : VIOLENCE_REASON);

    // A possible minor in a sexual/exploitative context must not be filed as NSFW or
    // Violence by a one-click tile: MINOR is what closes the account and opens the
    // child-safety referral. Offer the picker instead of silently mis-coding it.
    if (childSafetySignal(video) && defaultReason !== MINOR_REASON) {
      const fileAnyway = window.confirm(
        'The AI flagged a possible MINOR in a sexual or exploitative context.\n\n' +
          'Child-safety content must be removed as MINOR (closes the account and opens a child-safety referral).\n\n' +
          `OK = block as ${defaultReason.split(' ')[0]} anyway.  Cancel = open the reason picker.`
      );
      if (!fileAnyway) {
        setSelectedVideo(video);
        setSelectedAction('blocked');
        setReasonInput('');
        return;
      }
    }

    try {
      const res = await fetchWithAuth(`${getApiBaseUrl()}/video/t/updateVideo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ItemID: video.id,
          Action: 'blocked',
          VideoStatusReason: defaultReason,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(refusalText(res.status, j.message, 'Failed to block video'));

      toast.success(`Video blocked (${defaultReason.split(' ')[0]})`);
      setLastAction({ videoId: video.id, action: 'BLOCKED', timestamp: Date.now() });
      // Track blocked in session
      setBlockedInSession((prev) => new Set(prev).add(video.id));
      // Update local state
      setVideos((prevVideos) =>
        prevVideos.map((v) =>
          v.id === video.id ? { ...v, VideoStatus: 'blocked' } : v
        )
      );
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  // Quick action: Suspend user AND block all their videos on current view
  const quickSuspendUser = async (video: VideoItem) => {
    const userId = video.UserID || video.UserId;
    if (!userId) {
      toast.error('User ID not found');
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found');
      return;
    }

    const code = tileReason(userId);
    // NSFW text for sexual content, otherwise the label of the reason the person is shown.
    const blockReason = code === DEFAULT_BULK_REASON ? NSFW_REASON : reasonLabel(code);
    try {
      // 1. Suspend through the gated route. Only "already suspended" lets us carry on; any other refusal
      //    (403, a blocked account, a failure) stops here: nothing is marked suspended and no video is blocked.
      const out = await suspendAccount(userId, code, bulkNote(blockReason, code));
      if (out.already) toast.info(out.message);
      else if (out.revocation) (out.revocation.warn ? toast.warn : toast.info)(out.revocation.text);
      clearTileReason(userId);

      // Track suspended user
      setSuspendedUsers((prev) => new Set(prev).add(userId));

      // 2. Block all videos from this user on current view
      const userVideos = videos.filter((v) => (v.UserID || v.UserId) === userId && v.VideoStatus !== 'blocked');

      const blockedIds = new Set<string>();
      for (const userVideo of userVideos) {
        try {
          const blockRes = await fetchWithAuth(`${getApiBaseUrl()}/video/t/updateVideo`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ItemID: userVideo.id,
              Action: 'blocked',
              VideoStatusReason: blockReason,
            }),
          });
          if (blockRes.ok) {
            blockedIds.add(userVideo.id);
            setBlockedInSession((prev) => new Set(prev).add(userVideo.id));
          }
        } catch (e) {
          // Continue blocking other videos even if one fails
        }
      }

      // Mark only what the server confirmed: a tile must never say BLOCKED for a call that failed.
      setVideos((prevVideos) =>
        prevVideos.map((v) => (blockedIds.has(v.id) ? { ...v, VideoStatus: 'blocked' } : v))
      );

      const blockedCount = blockedIds.size;
      const failed = userVideos.length - blockedCount;
      const blockedText = `${blockedCount} video${blockedCount !== 1 ? 's' : ''} blocked`;
      if (failed > 0) {
        toast.warn(`User suspended + ${blockedText}, but ${failed} video${failed !== 1 ? 's' : ''} could not be blocked`);
      } else {
        toast.success(`User suspended + ${blockedText}`);
      }
    } catch (err: any) {
      toast.error(accountActionError(err));
    }
  };

  // Quick action: Activate (unsuspend) user
  const quickActivateUser = async (video: VideoItem) => {
    const userId = video.UserID || video.UserId;
    if (!userId) {
      toast.error('User ID not found');
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found');
      return;
    }

    try {
      // Gated restore; a 409 ("not suspended", e.g. blocked) falls back to the old moderator-gated route and reports its answer.
      const out = await restoreAccount(userId, bulkNote('account restored from the review queue'));
      if (out.via === 'legacy' && out.message) toast.info(out.message);

      // Remove from suspended users set
      setSuspendedUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });

      toast.success('User activated');
    } catch (err: any) {
      toast.error(accountActionError(err));
    }
  };

  // Quick action: Approve video
  const quickApproveVideo = async (video: VideoItem) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found');
      return;
    }

    try {
      const res = await fetchWithAuth(`${getApiBaseUrl()}/video/t/updateVideo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ItemID: video.id,
          Action: 'active',
          VideoStatusReason: reasonValue('ACTIVE'),
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(refusalText(res.status, j.message, 'Failed to approve video'));

      toast.success('Video approved');
      setLastAction({ videoId: video.id, action: 'ACTIVE', timestamp: Date.now() });
      // Update local state
      setVideos((prevVideos) =>
        prevVideos.map((v) =>
          v.id === video.id ? { ...v, VideoStatus: 'active' } : v
        )
      );
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  // Approve all videos (skipping blocked videos and videos from suspended users)
  const approveAllVideos = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found');
      return;
    }

    setApproveAllLoading(true);
    let approved = 0;
    let skipped = 0;
    let failed = 0;

    // Filter videos to approve
    const videosToProcess = videos.filter((video) => {
      const userId = video.UserID || video.UserId || '';

      // Skip if video is already blocked
      if (video.VideoStatus === 'blocked') {
        return false;
      }

      // Skip if video was blocked in this session
      if (blockedInSession.has(video.id)) {
        return false;
      }

      // Skip if user is suspended
      if (suspendedUsers.has(userId)) {
        return false;
      }

      // Skip if video is already active/approved
      if (video.VideoStatus === 'active') {
        return false;
      }

      return true;
    });

    const totalToProcess = videosToProcess.length;
    const totalSkipped = videos.length - totalToProcess;

    setApproveAllProgress({ current: 0, total: totalToProcess, skipped: totalSkipped });

    if (totalToProcess === 0) {
      toast.info(`All ${videos.length} videos were skipped (blocked, suspended users, or already approved)`);
      setApproveAllLoading(false);
      setApproveAllProgress(null);
      return;
    }

    toast.info(`Approving ${totalToProcess} videos (skipping ${totalSkipped})...`);

    for (let i = 0; i < videosToProcess.length; i++) {
      const video = videosToProcess[i];
      const userId = video.UserID || video.UserId || '';

      // Double-check: Skip if user got suspended during the loop
      if (suspendedUsers.has(userId)) {
        skipped++;
        setApproveAllProgress({ current: i + 1, total: totalToProcess, skipped: totalSkipped + skipped });
        continue;
      }

      // Double-check: Skip if video got blocked during the loop
      if (blockedInSession.has(video.id)) {
        skipped++;
        setApproveAllProgress({ current: i + 1, total: totalToProcess, skipped: totalSkipped + skipped });
        continue;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);
      try {
        const res = await fetchWithAuth(`${getApiBaseUrl()}/video/t/updateVideo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ItemID: video.id,
            Action: 'active',
            VideoStatusReason: reasonValue('ACTIVE'),
          }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        const j = await res.json();

        if (res.ok) {
          approved++;
          // Update local state
          setVideos((prevVideos) =>
            prevVideos.map((v) =>
              v.id === video.id ? { ...v, VideoStatus: 'active' } : v
            )
          );
        } else {
          console.error(`Failed to approve video ${video.id}:`, j.message);
          failed++;
        }
      } catch (err: any) {
        clearTimeout(timeoutId);
        if (err?.name === "AbortError") { console.warn(`Approve timeout for video ${video.id} (>20s)`); } else { console.error(`Error approving video ${video.id}:`, err.message); }
        failed++;
      }

      setApproveAllProgress({ current: i + 1, total: totalToProcess, skipped: totalSkipped + skipped });

      // Small delay between requests to avoid rate limiting
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    setApproveAllLoading(false);
    setApproveAllProgress(null);

    if (failed > 0) {
      toast.warning(`Approved ${approved} videos, skipped ${totalSkipped + skipped}, failed ${failed}`);
    } else {
      toast.success(`Successfully approved ${approved} videos! (Skipped ${totalSkipped + skipped})`);
    }
  };

  return (
    <Fragment>
      <Navbar />
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
        {/* Background decoration */}
        <div className='fixed inset-0 overflow-hidden pointer-events-none'>
          <div className='absolute -top-40 -right-40 w-80 h-80 bg-[#FF9C15]/10 rounded-full blur-3xl' />
          <div className='absolute -bottom-40 -left-40 w-80 h-80 bg-[#F42026]/10 rounded-full blur-3xl' />
        </div>

        {/* Header */}
        <div className='relative bg-white/5 backdrop-blur-sm border-b border-white/10'>
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between'>
              <div>
                <h1 className='text-3xl font-bold text-white'>Bulk Review</h1>
                <p className='mt-1 text-sm text-gray-400'>Review multiple videos at once</p>
              </div>
              <div className='mt-4 sm:mt-0 flex items-center gap-3'>
                <div className='flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg border border-white/10'>
                  <span className='text-sm font-medium text-gray-300'>Page</span>
                  <span className='inline-flex items-center justify-center min-w-[2rem] h-8 px-2 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-bold rounded-md'>
                    {page}
                  </span>
                </div>
                <div className='flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg border border-white/10'>
                  <span className='text-sm font-medium text-gray-300'>Videos</span>
                  <span className='inline-flex items-center justify-center min-w-[2rem] h-8 px-2 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-bold rounded-md'>
                    {videos.length}
                  </span>
                </div>
                <div className='flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg border border-white/10'>
                  <span className='text-sm font-medium text-gray-300'>Auto-Reviewed</span>
                  <span className='inline-flex items-center justify-center min-w-[2rem] h-8 px-2 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-bold rounded-md'>
                    {reviewedInBackground.size}/{videos.length}
                  </span>
                </div>
                {pendingReviews.size > 0 && (
                  <div className='flex items-center gap-2 bg-amber-500/20 px-4 py-2 rounded-lg border border-amber-500/30 animate-pulse'>
                    <span className='text-sm font-medium text-gray-300'>Processing</span>
                    <span className='inline-flex items-center justify-center min-w-[2rem] h-8 px-2 bg-amber-600 text-white font-bold rounded-md'>
                      {pendingReviews.size}
                    </span>
                  </div>
                )}
                {failedReviews.size > 0 && (
                  <div className='flex items-center gap-2 bg-red-500/20 px-4 py-2 rounded-lg border border-red-500/30'>
                    <span className='text-sm font-medium text-gray-300'>Failed</span>
                    <span className='inline-flex items-center justify-center min-w-[2rem] h-8 px-2 bg-red-600 text-white font-bold rounded-md'>
                      {failedReviews.size}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
          {/* Auto-Review Toggle */}
          <div className='bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 mb-6 overflow-hidden'>
            <div className='bg-gradient-to-r from-[#FF9C15]/20 to-[#F42026]/20 px-6 py-4 border-b border-white/10'>
              <div className='flex items-center justify-between'>
                <div>
                  <h2 className='text-lg font-semibold text-white'>Auto-Review Settings</h2>
                  <p className='text-sm text-gray-400 mt-1'>
                    Automatically mark videos as reviewed in the background
                  </p>
                </div>
                <button
                  onClick={() => setAutoReviewEnabled(!autoReviewEnabled)}
                  className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-800 ${
                    autoReviewEnabled
                      ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] focus:ring-[#FF9C15]'
                      : 'bg-gray-600 focus:ring-gray-400'
                  }`}
                  aria-pressed={autoReviewEnabled}
                  aria-label='Toggle auto-review'
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
                      autoReviewEnabled ? 'translate-x-9' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <div className='px-6 py-4'>
              <div className='flex items-center gap-3'>
                <div
                  className={`px-4 py-2 rounded-lg font-semibold ${
                    autoReviewEnabled
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : 'bg-white/10 text-gray-300 border border-white/20'
                  }`}
                >
                  Status: {autoReviewEnabled ? '✅ AUTO-REVIEW ON' : '⏸️ AUTO-REVIEW OFF'}
                </div>
                {!autoReviewEnabled && (
                  <p className='text-sm text-amber-400 bg-amber-500/20 px-3 py-2 rounded-lg border border-amber-500/30'>
                    ⚠️ Videos will not be auto-reviewed. You must manually review each video.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className='bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 mb-6 overflow-hidden'>
            <div className='bg-gradient-to-r from-[#FF9C15]/20 to-[#F42026]/20 px-6 py-4 border-b border-white/10'>
              <h2 className='text-lg font-semibold text-white'>Filters</h2>
            </div>
            <div className='p-6'>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-300 mb-2'>User ID</label>
                  <input
                    type='text'
                    value={searchUserIdInput}
                    onChange={(e) => setSearchUserIdInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    placeholder='Enter User ID'
                    className='w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-[#FF9C15] focus:border-[#FF9C15] transition-all text-white placeholder-gray-400 font-mono text-sm'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-300 mb-2'>Username</label>
                  <input
                    type='text'
                    value={searchUserNameInput}
                    onChange={(e) => setSearchUserNameInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    placeholder='Enter Username'
                    className='w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-[#FF9C15] focus:border-[#FF9C15] transition-all text-white placeholder-gray-400 text-sm'
                  />
                </div>
                <div className='flex items-end'>
                  <button
                    onClick={handleSearch}
                    disabled={!searchUserIdInput.trim() && !searchUserNameInput.trim()}
                    className='w-full px-6 py-2.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2'
                  >
                    <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
                    </svg>
                    Search
                  </button>
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-300 mb-2'>Review Status</label>
                  <select
                    value={reviewedFilter}
                    onChange={(e) => setReviewedFilter(e.target.value as any)}
                    className='w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-[#FF9C15] focus:border-[#FF9C15] transition-all text-white'
                  >
                    <option value='all' className='bg-slate-800'>All Videos</option>
                    <option value='true' className='bg-slate-800'>Reviewed</option>
                    <option value='false' className='bg-slate-800'>Unreviewed</option>
                  </select>
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-300 mb-2'>Video Status</label>
                  <select
                    value={videoStatusFilter}
                    onChange={(e) => chooseVideoStatus(e.target.value)}
                    className='w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-[#FF9C15] focus:border-[#FF9C15] transition-all text-white'
                  >
                    <option value='' className='bg-slate-800'>All Statuses</option>
                    {STATUSES.map((s) => (
                      <option key={s} value={s} className='bg-slate-800'>
                        {s.replace('_', ' ').toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {(userIdFilter.trim() || userNameFilter.trim()) && (
                <div className='mt-4 flex items-center gap-4 flex-wrap'>
                  <span className='text-sm text-gray-400'>Active filters:</span>
                  {userIdFilter.trim() && (
                    <div className='flex items-center gap-2'>
                      <span className='text-sm text-gray-400'>User ID:</span>
                      <span className='px-2 py-1 bg-[#FF9C15]/20 border border-[#FF9C15]/40 rounded text-sm font-mono text-[#FF9C15]'>{userIdFilter.trim()}</span>
                    </div>
                  )}
                  {userNameFilter.trim() && (
                    <div className='flex items-center gap-2'>
                      <span className='text-sm text-gray-400'>Username:</span>
                      <span className='px-2 py-1 bg-[#FF9C15]/20 border border-[#FF9C15]/40 rounded text-sm text-[#FF9C15]'>{userNameFilter.trim()}</span>
                    </div>
                  )}
                  <button
                    onClick={handleClearSearch}
                    className='text-sm text-red-400 hover:text-red-300 underline'
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Videos Grid */}
          {!videoStatusFilter && !userIdFilter.trim() && !userNameFilter.trim() ? (
            <div className='flex flex-col items-center justify-center py-20 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10'>
              <div className='text-center space-y-6 max-w-md'>
                <div className='flex justify-center'>
                  <div className='w-24 h-24 bg-gradient-to-br from-[#FF9C15] to-[#F42026] rounded-full flex items-center justify-center shadow-xl'>
                    <svg className='w-12 h-12 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z' />
                    </svg>
                  </div>
                </div>
                <div>
                  <h2 className='text-2xl font-bold text-white mb-2'>Select a Filter</h2>
                  <p className='text-gray-400'>Enter a User ID, Username, or select a video status to start reviewing videos.</p>
                </div>
                <div className='flex flex-wrap gap-2 justify-center'>
                  {['active', 'review_failed', 'review_required', 'under_review', 'processing'].map((s) => (
                    <button
                      key={s}
                      onClick={() => chooseVideoStatus(s)}
                      className='px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg border border-white/20 hover:border-white/40 transition-all duration-200'
                    >
                      {s.replace(/_/g, ' ').toUpperCase()}
                    </button>
                  ))}
                </div>
                <p className='text-gray-500 text-sm'>Or use the dropdown above for more options</p>
              </div>
            </div>
          ) : loading ? (
            <div className='text-center py-12'>
              <div className='inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9C15]'></div>
              <p className='mt-4 text-gray-400'>Loading videos...</p>
            </div>
          ) : allVideosReviewed ? (
            <div className='flex flex-col items-center justify-center py-20 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10'>
              <div className='text-center space-y-6 max-w-md'>
                <div className='flex justify-center'>
                  <div className='w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-xl'>
                    <svg className='w-12 h-12 text-white' fill='currentColor' viewBox='0 0 20 20'>
                      <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
                    </svg>
                  </div>
                </div>
                <div>
                  <h2 className='text-3xl font-bold text-gray-800 mb-2'>All Videos Reviewed!</h2>
                  <p className='text-gray-600'>You've reviewed all available videos. Check back later for new content.</p>
                </div>
                <button
                  onClick={() => loadVideos(1)}
                  className='px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 mx-auto'
                >
                  <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
                    <path fillRule='evenodd' d='M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z' clipRule='evenodd' />
                  </svg>
                  Refresh for New Videos
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6'>
                {videos.map((video) => (
                  <div key={video.id} className='bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden group'>
                    <div
                      className='aspect-[9/16] relative bg-gray-900 cursor-pointer'
                      onMouseEnter={(e) => {
                        const videoEl = e.currentTarget.querySelector('video');
                        if (videoEl) videoEl.play().catch(() => {});
                      }}
                      onMouseLeave={(e) => {
                        const videoEl = e.currentTarget.querySelector('video');
                        if (videoEl) {
                          videoEl.pause();
                          videoEl.currentTime = 0;
                          setVideoProgress((prev) => ({ ...prev, [video.id]: 0 }));
                        }
                      }}
                    >
                      {getVideoThumbnail(video) ? (
                        <video
                          src={getVideoThumbnail(video)}
                          className='w-full h-full object-cover'
                          muted
                          onTimeUpdate={(e) => {
                            const progress = (e.currentTarget.currentTime / e.currentTarget.duration) * 100;
                            setVideoProgress((prev) => ({ ...prev, [video.id]: progress }));
                          }}
                        />
                      ) : (
                        <div className='w-full h-full flex flex-col items-center justify-center bg-gray-800 text-gray-400'>
                          <svg className='w-12 h-12 mb-2 opacity-50' fill='currentColor' viewBox='0 0 20 20'>
                            <path fillRule='evenodd' d='M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm3 2h6v4H7V5zm8 8v2h1v-2h-1zm-2-2H7v4h6v-4zm2 0h1V9h-1v2zm1-4V5h-1v2h1zM5 5v2H4V5h1zm-1 4v2h1V9H4zm1 4H4v2h1v-2z' clipRule='evenodd' />
                          </svg>
                          <span className='text-[10px]'>No video</span>
                        </div>
                      )}
                      {/* Action Indicator - Top Left Corner (shows on the actioned video) */}
                      {lastAction && lastAction.videoId === video.id && (
                        <div className='absolute top-2 left-2 z-20 animate-pulse pointer-events-none'>
                          <div
                            className={`flex items-center gap-1 px-2 py-1 rounded-full shadow-2xl border border-white/30 font-bold text-white text-xs ${
                              lastAction.action === 'ACTIVE' || lastAction.action === 'APPROVED'
                                ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                                : lastAction.action === 'BLOCKED'
                                ? 'bg-gradient-to-r from-yellow-500 to-orange-600'
                                : lastAction.action === 'DELETED'
                                ? 'bg-gradient-to-r from-red-500 to-red-700'
                                : 'bg-gradient-to-r from-blue-500 to-indigo-600'
                            }`}
                          >
                            {lastAction.action === 'ACTIVE' || lastAction.action === 'APPROVED' ? (
                              <span>✅ APPROVED</span>
                            ) : lastAction.action === 'BLOCKED' ? (
                              <span>🚫 BLOCKED</span>
                            ) : lastAction.action === 'DELETED' ? (
                              <span>🗑️ DELETED</span>
                            ) : (
                              <span>📋 {lastAction.action}</span>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Video Type Icon */}
                      <div className='absolute top-2 right-2 pointer-events-none'>
                        {video.VideoType?.toUpperCase() === 'PINGS' || video.VideoType?.toUpperCase() === 'PING' ? (
                          <div className='flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full shadow-lg'>
                            <svg className='w-4 h-4 text-white' fill='currentColor' viewBox='0 0 20 20'>
                              <path
                                fillRule='evenodd'
                                d='M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z'
                                clipRule='evenodd'
                              />
                            </svg>
                          </div>
                        ) : video.VideoType?.toUpperCase() === 'STORIES' || video.VideoType?.toUpperCase() === 'STORY' ? (
                          <div className='flex items-center justify-center w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full shadow-lg'>
                            <svg className='w-4 h-4 text-white' fill='currentColor' viewBox='0 0 20 20'>
                              <path d='M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z' />
                            </svg>
                          </div>
                        ) : video.VideoType?.toUpperCase() === 'ARCHIVE' ? (
                          <div className='flex items-center justify-center w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full shadow-lg'>
                            <svg className='w-4 h-4 text-white' fill='currentColor' viewBox='0 0 20 20'>
                              <path d='M4 3a2 2 0 100 4h12a2 2 0 100-4H4z' />
                              <path
                                fillRule='evenodd'
                                d='M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z'
                                clipRule='evenodd'
                              />
                            </svg>
                          </div>
                        ) : null}
                      </div>

                      {/* Status Badges */}
                      {video.VideoStatus === 'blocked' && (
                        <div className='absolute top-2 left-2 px-2 py-1 bg-yellow-500 rounded-md shadow-lg pointer-events-none'>
                          <span className='text-xs font-bold text-white'>🚫 BLOCKED</span>
                        </div>
                      )}
                      {video.VideoStatus === 'deleted' && (
                        <div className='absolute top-2 left-2 px-2 py-1 bg-red-600 rounded-md shadow-lg pointer-events-none'>
                          <span className='text-xs font-bold text-white'>🗑️ DELETED</span>
                        </div>
                      )}
                      {video.VideoStatus === 'active' && (
                        <div className='absolute top-2 left-2 px-2 py-1 bg-green-500 rounded-md shadow-lg pointer-events-none'>
                          <span className='text-xs font-bold text-white'>✅ APPROVED</span>
                        </div>
                      )}

                      {/* Background Review Status Badge */}
                      {reviewedInBackground.has(video.id) && (
                        <div className='absolute bottom-2 left-2 px-2 py-1 bg-green-500 rounded-md shadow-lg pointer-events-none'>
                          <span className='text-xs font-bold text-white'>✓ Reviewed</span>
                        </div>
                      )}
                      {pendingReviews.has(video.id) && !reviewedInBackground.has(video.id) && !failedReviews.has(video.id) && (
                        <div className='absolute bottom-2 left-2 px-2 py-1 bg-blue-500 rounded-md shadow-lg pointer-events-none'>
                          <span className='text-xs font-bold text-white'>⏳ Reviewing...</span>
                        </div>
                      )}
                      {failedReviews.has(video.id) && (
                        <div className='absolute bottom-2 left-2 px-2 py-1 bg-red-500 rounded-md shadow-lg pointer-events-none'>
                          <span className='text-xs font-bold text-white'>✗ Failed</span>
                        </div>
                      )}

                      {/* Video Controls Overlay (shown on hover) */}
                      <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-200'>
                        {/* Bottom Controls Bar */}
                        <div className='absolute bottom-0 left-0 right-0 p-2 space-y-1'>
                          {/* Progress Bar */}
                          <input
                            type='range'
                            min='0'
                            max='100'
                            value={videoProgress[video.id] || 0}
                            onChange={(e) => {
                              const videoEl = e.currentTarget.closest('.aspect-\\[9\\/16\\]')?.querySelector('video');
                              if (videoEl && videoEl.duration) {
                                const newTime = (parseFloat(e.target.value) / 100) * videoEl.duration;
                                videoEl.currentTime = newTime;
                              }
                            }}
                            className='w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer'
                            style={{
                              background: `linear-gradient(to right, #3b82f6 ${videoProgress[video.id] || 0}%, #4b5563 ${videoProgress[video.id] || 0}%)`,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          {/* Time and Fullscreen */}
                          <div className='flex items-center justify-between text-white text-xs'>
                            <span className='font-mono'>
                              {Math.floor((videoProgress[video.id] || 0) / 100)}s
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openVideoModal(video);
                              }}
                              className='p-1 hover:bg-white/20 rounded transition-colors'
                              title='Open fullscreen'
                            >
                              <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 20 20'>
                                <path d='M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z' />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='p-2 space-y-1'>
                      <div className='flex items-center gap-1'>
                        <p className='text-xs text-gray-600 truncate font-semibold'>{video.UserName || 'Unknown'}</p>
                        {suspendedUsers.has(video.UserID || video.UserId || '') && (
                          <span className='px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded'>
                            SUSPENDED
                          </span>
                        )}
                      </div>

                      {/* Country */}
                      <p className='text-[10px] text-gray-500 truncate'>{video.Country || 'N/A'}</p>

                      {/* Creation Date */}
                      <div className='flex items-center gap-1 text-[10px] text-gray-500'>
                        <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' />
                        </svg>
                        <span>
                          {video.createdAt || video.CreatedAt
                            ? new Date(video.createdAt || video.CreatedAt).toLocaleString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'N/A'}
                        </span>
                      </div>

                      {childSafetySignal(video) && (
                        <div className='bg-red-600 text-white rounded px-1.5 py-1 text-[8px] font-bold leading-tight'>
                          ⚠ Possible minor — review as MINOR (child safety)
                        </div>
                      )}

                      {/* Video Status Reason - Show 2 lines, tooltip only if truncated */}
                      {video.VideoStatusReason && (
                        <div
                          className='bg-amber-50 border border-amber-200 rounded px-1.5 py-1 group/reason relative cursor-default'
                          title={video.VideoStatusReason.length > 60 ? video.VideoStatusReason : undefined}
                        >
                          <p className='text-[8px] text-amber-800 font-medium line-clamp-2 leading-tight'>
                            {video.VideoStatusReason}
                          </p>
                          {/* Full text tooltip on hover - only show if text is long enough to be truncated */}
                          {video.VideoStatusReason.length > 60 && (
                            <div className='absolute left-0 right-0 bottom-full mb-1 hidden group-hover/reason:block z-50 pointer-events-none'>
                              <div className='bg-gray-900 text-white text-[9px] p-2 rounded shadow-lg max-w-xs'>
                                {video.VideoStatusReason}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <p className='text-[9px] text-gray-400 truncate font-mono'>{video.id}</p>

                      {/* Quick action buttons - With text labels */}
                      <div className='space-y-1 mt-1'>
                        {/* Row 1: one-click block reasons — NSFW and Violence */}
                        <div className='grid grid-cols-2 gap-0.5'>
                          <button
                            onClick={() => quickBlockVideo(video, NSFW_REASON)}
                            className='px-1 py-1.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white text-[8px] font-semibold rounded transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-0.5'
                            title='Block — NSFW / Nudity / Sexual Content'
                          >
                            <span>🚫</span>
                            <span>NSFW</span>
                          </button>
                          <button
                            onClick={() => quickBlockVideo(video, VIOLENCE_REASON)}
                            className='px-1 py-1.5 bg-gradient-to-r from-rose-600 to-red-800 hover:from-rose-700 hover:to-red-900 text-white text-[8px] font-semibold rounded transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-0.5'
                            title='Block — Violent or Graphic Content'
                          >
                            <span>🩸</span>
                            <span>Violence</span>
                          </button>
                        </div>
                        {/* Row 2: Duplicate, Reason Block, Approve */}
                        <div className='grid grid-cols-3 gap-0.5'>
                          <button
                            onClick={() => quickBlockVideo(video, DUPLICATE_REASON)}
                            className='px-1 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white text-[8px] font-semibold rounded transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-0.5'
                            title='Block — Duplicate Content'
                          >
                            <span>🔁</span>
                            <span>Dup</span>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedVideo(video);
                              setSelectedAction('blocked');
                              setReasonInput('');
                            }}
                            className='px-1 py-1.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-[8px] font-semibold rounded transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-0.5'
                            title='Block with another reason'
                          >
                            <span>📝</span>
                            <span>Reason</span>
                          </button>
                          <button
                            onClick={() => quickApproveVideo(video)}
                            className='px-1 py-1.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-[8px] font-semibold rounded transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-0.5'
                            title='Approve'
                          >
                            <span>✅</span>
                            <span>Approve</span>
                          </button>
                        </div>
                        {/* Row 3: Suspend/Activate User, View Details */}
                        <div className='grid grid-cols-2 gap-0.5'>
                          {suspendedUsers.has(video.UserID || video.UserId || '') ? (
                            <button
                              onClick={() => quickActivateUser(video)}
                              className='px-1 py-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-[9px] font-semibold rounded transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-0.5'
                              title='Activate user (unsuspend)'
                            >
                              <span>✅</span>
                              <span>Activate</span>
                            </button>
                          ) : (
                            <>
                            <select
                              value={tileReason(video.UserID || video.UserId || '')}
                              onChange={(e) =>
                                setTileReasons((prev) => ({ ...prev, [video.UserID || video.UserId || '']: e.target.value as AccountReasonCode }))
                              }
                              className='col-span-2 w-full px-1 py-0.5 bg-white border border-gray-300 rounded text-[11px] text-gray-800'
                              title={`Reason shown to the person: ${reasonLabel(tileReason(video.UserID || video.UserId || ''))}`}
                              aria-label='Reason shown to the user'
                            >
                              {ACCOUNT_REASONS.map((r) => (
                                <option key={r.code} value={r.code} title={r.label}>
                                  {r.label}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => quickSuspendUser(video)}
                              className='px-1 py-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-[9px] font-semibold rounded transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-0.5'
                              title='Suspend user, ban their phones too, and block all their videos on this page'
                            >
                              <span>⛔</span>
                              <span>Suspend</span>
                            </button>
                            </>
                          )}
                          <button
                            onClick={() => openVideoModal(video)}
                            className='px-1 py-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white text-[9px] font-semibold rounded transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-0.5'
                          >
                            <span>👁️</span>
                            <span>Details</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation */}
              <div className='flex justify-between items-center'>
                <button
                  onClick={() => loadVideos(Math.max(1, page - 1))}
                  disabled={page === 1 || loading}
                  className='px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed'
                >
                  ← Previous Page
                </button>

                <div className='flex items-center gap-3'>
                  {/* Approve All Button */}
                  <button
                    onClick={approveAllVideos}
                    disabled={loading || approveAllLoading || videos.length === 0}
                    className='px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2'
                  >
                    {approveAllLoading ? (
                      <>
                        <svg className='animate-spin h-5 w-5' fill='none' viewBox='0 0 24 24'>
                          <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                          <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                        </svg>
                        {approveAllProgress ? `${approveAllProgress.current}/${approveAllProgress.total}` : 'Approving...'}
                      </>
                    ) : (
                      <>
                        <span>✅</span>
                        Approve All
                      </>
                    )}
                  </button>

                  <button
                    onClick={markAllReviewedAndNext}
                    disabled={loading || videos.length === 0}
                    className='px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed'
                  >
                    Done & Load Fresh Videos
                  </button>

                  <button
                    onClick={() => loadVideos(page + 1)}
                    disabled={loading || videos.length < PAGE_SIZE}
                    className='px-6 py-3 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-xl hover:from-[#e88c13] hover:to-[#d91c22] transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed'
                  >
                    Next Page →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Video Review Modal */}
      {isModalOpen && selectedVideo && (
        <div className='fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4' onClick={() => setIsModalOpen(false)}>
          <div className='bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col' onClick={(e) => e.stopPropagation()}>
            <div className='bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex-shrink-0'>
              <div className='flex justify-between items-center'>
                <div className='flex items-center gap-3'>
                  <div>
                    <h3 className='text-xl font-bold text-white'>Review Video</h3>
                    <div className='flex items-center gap-2 mt-1'>
                      <p className='text-blue-100 text-sm'>{selectedVideo.UserName || 'Unknown User'}</p>
                      {/* Suspended Badge */}
                      {suspendedUsers.has(selectedVideo.UserID || selectedVideo.UserId || '') && (
                        <span className='px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse'>
                          SUSPENDED
                        </span>
                      )}
                      <span className='text-blue-200'>•</span>
                      <p className='text-blue-100 text-sm'>{selectedVideo.Country || 'N/A'}</p>
                    </div>
                  </div>
                  {/* Video Type Icon */}
                  {selectedVideo.VideoType?.toUpperCase() === 'PINGS' || selectedVideo.VideoType?.toUpperCase() === 'PING' ? (
                    <div className='w-10 h-10 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full flex items-center justify-center shadow-lg'>
                      <svg className='w-5 h-5 text-white' fill='currentColor' viewBox='0 0 20 20'>
                        <path fillRule='evenodd' d='M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z' clipRule='evenodd' />
                      </svg>
                    </div>
                  ) : selectedVideo.VideoType?.toUpperCase() === 'STORIES' || selectedVideo.VideoType?.toUpperCase() === 'STORY' ? (
                    <div className='w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center shadow-lg'>
                      <svg className='w-5 h-5 text-white' fill='currentColor' viewBox='0 0 20 20'>
                        <path d='M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z' />
                      </svg>
                    </div>
                  ) : selectedVideo.VideoType?.toUpperCase() === 'ARCHIVE' ? (
                    <div className='w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full flex items-center justify-center shadow-lg'>
                      <svg className='w-5 h-5 text-white' fill='currentColor' viewBox='0 0 20 20'>
                        <path d='M4 3a2 2 0 100 4h12a2 2 0 100-4H4z' />
                        <path fillRule='evenodd' d='M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z' clipRule='evenodd' />
                      </svg>
                    </div>
                  ) : null}
                </div>
                <button onClick={() => setIsModalOpen(false)} className='text-white hover:text-gray-200'>
                  <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                  </svg>
                </button>
              </div>
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 overflow-y-auto'>
              {/* Video Player */}
              <div className='flex items-start justify-center'>
                <div className='w-full max-w-sm bg-black rounded-xl overflow-hidden shadow-2xl'>
                  <div className='aspect-[9/16] relative'>
                    <video
                      src={`${selectedVideo.UploadedFromR2 === true ? 'https://cdn2.pingtop.co' : 'https://cdn.pingtop.co'}${selectedVideo.url}`}
                      controls
                      autoPlay
                      loop
                      className='w-full h-full object-cover'
                    />
                    {/* Action Indicator - Top Left Corner */}
                    {lastAction && lastAction.videoId === selectedVideo.id && (
                      <div className='absolute top-3 left-3 z-20 animate-pulse'>
                        <div
                          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-2xl border-2 border-white/30 font-bold text-white text-sm ${
                            lastAction.action === 'ACTIVE' || lastAction.action === 'APPROVED'
                              ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                              : lastAction.action === 'BLOCKED'
                              ? 'bg-gradient-to-r from-yellow-500 to-orange-600'
                              : lastAction.action === 'DELETED'
                              ? 'bg-gradient-to-r from-red-500 to-red-700'
                              : 'bg-gradient-to-r from-blue-500 to-indigo-600'
                          }`}
                        >
                          {lastAction.action === 'ACTIVE' || lastAction.action === 'APPROVED' ? (
                            <span>✅ APPROVED</span>
                          ) : lastAction.action === 'BLOCKED' ? (
                            <span>🚫 BLOCKED</span>
                          ) : lastAction.action === 'DELETED' ? (
                            <span>🗑️ DELETED</span>
                          ) : (
                            <span>📋 {lastAction.action}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className='space-y-4'>
                <div>
                  <h4 className='text-lg font-semibold text-gray-800 mb-3'>Quick Actions</h4>
                  <div className='grid grid-cols-2 gap-3'>
                    <button
                      onClick={() => confirmAction('active', 'ACTIVE - Safe Video: Video is safe')}
                      disabled={actionLoading}
                      className='p-4 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40'
                    >
                      <span className='text-2xl mb-2 block'>✅</span>
                      <span className='text-sm font-semibold'>Approve</span>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedAction('blocked');
                        // Set default reason based on video status
                        const videoStatus = selectedVideo?.VideoStatus?.toLowerCase();
                        if (videoStatus === 'review_required') {
                          setReasonInput('NSFW - Nudity / Sexual Content: Contains explicit or adult material');
                        } else {
                          setReasonInput('VIOLENCE - Violent or Graphic Content: Shows physical harm or violent behavior');
                        }
                      }}
                      disabled={actionLoading}
                      className='p-4 bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-xl hover:from-yellow-600 hover:to-yellow-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40'
                    >
                      <span className='text-2xl mb-2 block'>🚫</span>
                      <span className='text-sm font-semibold'>Block</span>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedAction('deleted');
                        setReasonInput('');
                      }}
                      disabled={actionLoading}
                      className='p-4 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40'
                    >
                      <span className='text-2xl mb-2 block'>🗑️</span>
                      <span className='text-sm font-semibold'>Delete</span>
                    </button>
                    <button
                      onClick={openUserStatusModal}
                      disabled={actionLoading}
                      className='p-4 bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-xl hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40'
                    >
                      <span className='text-2xl mb-2 block'>👤</span>
                      <span className='text-sm font-semibold'>User Status</span>
                    </button>
                  </div>
                </div>

                {selectedAction && (
                  <div className='bg-gray-50 rounded-xl p-4'>
                    <h4 className='text-md font-semibold text-gray-800 mb-3'>Select Reason</h4>
                    <select
                      value={reasonInput}
                      onChange={(e) => setReasonInput(e.target.value)}
                      className='w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all mb-3'
                    >
                      <option value=''>Select a reason...</option>
                      {REASONS.map((r) => (
                        <option key={r.code} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => confirmAction()}
                      disabled={!reasonInput.trim() || actionLoading}
                      className='w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md'
                    >
                      {actionLoading ? 'Processing...' : `Confirm ${selectedAction}`}
                    </button>
                  </div>
                )}

                {childSafetySignal(selectedVideo) && (
                  <div className='bg-red-50 border-2 border-red-600 rounded-xl p-4'>
                    <h4 className='text-md font-bold text-red-700 mb-1'>⚠ Possible child-safety content</h4>
                    <p className='text-sm text-red-800'>
                      The AI put a minor in a sexual or exploitative context. If a person confirms it, remove it as
                      <strong> MINOR</strong>: that closes the account and opens the child-safety referral. NSFW or
                      Violence records neither.
                    </p>
                  </div>
                )}

                {/* Video Status Reason - Most Important */}
                {selectedVideo.VideoStatusReason && (
                  <div className='bg-amber-50 border border-amber-200 rounded-xl p-4'>
                    <h4 className='text-md font-semibold text-amber-800 mb-2 flex items-center gap-2'>
                      <span>⚠️</span> Video Status Reason
                    </h4>
                    <p className='text-amber-900 font-medium'>{selectedVideo.VideoStatusReason}</p>
                  </div>
                )}

                {/* Video Info */}
                <div className='bg-gray-50 rounded-xl p-4 max-h-[300px] overflow-y-auto'>
                  <h4 className='text-md font-semibold text-gray-800 mb-3'>Video Details</h4>
                  <table className='w-full text-sm'>
                    <tbody className='divide-y divide-gray-200'>
                      {Object.entries(selectedVideo)
                        .filter(([k]) => k !== 'id' && k !== 'url' && k !== 'VideoStatusReason')
                        .map(([k, v]) => (
                          <tr key={k}>
                            <td className='py-2 font-semibold text-gray-700 w-1/3'>{k}</td>
                            <td className='py-2 text-gray-900 break-all'>{String(v)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Status Modal */}
      {userStatusModalOpen && (
        <div className='fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4'>
          <div className='bg-white rounded-2xl shadow-2xl w-full max-w-lg'>
            <div className='bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl'>
              <h3 className='text-xl font-bold text-white'>Update User Account Status</h3>
            </div>
            <div className='p-6 space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>User ID</label>
                <input
                  value={userIdInput}
                  onChange={(e) => setUserIdInput(e.target.value)}
                  className='w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-mono'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>Account Status</label>
                <select
                  value={userStatus}
                  onChange={(e) => setUserStatus(e.target.value as UserAccountStatus)}
                  className='w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all'
                >
                  <option value=''>Select status...</option>
                  {USER_ACCOUNT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              {userStatus === 'suspended' && (
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>Reason shown to the user</label>
                  <select
                    value={reasonCode}
                    onChange={(e) => setReasonCode(e.target.value as AccountReasonCode)}
                    className='w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all'
                  >
                    {ACCOUNT_REASONS.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <p className='text-xs text-gray-500 mt-1'>{REASON_SHOWN_NOTE}</p>
                  <p className='text-xs text-orange-700 mt-1'>Suspending also bans the person&apos;s phones.</p>
                </div>
              )}
              <div className='flex justify-end gap-3 pt-2'>
                <button
                  onClick={() => setUserStatusModalOpen(false)}
                  className='px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-all'
                >
                  Cancel
                </button>
                <button
                  onClick={updateUserAccountStatus}
                  disabled={!userIdInput || !userStatus || userStatusLoading}
                  className='px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md'
                >
                  {userStatusLoading ? 'Updating...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reason Block Modal - Quick inline modal for selecting block reason */}
      {selectedVideo && selectedAction === 'blocked' && !isModalOpen && (
        <div className='fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4' onClick={() => { setSelectedVideo(null); setSelectedAction(''); }}>
          <div className='bg-white rounded-2xl shadow-2xl w-full max-w-md' onClick={(e) => e.stopPropagation()}>
            <div className='bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 rounded-t-2xl'>
              <div className='flex items-center justify-between'>
                <div>
                  <h3 className='text-lg font-bold text-white'>Block Video with Reason</h3>
                  <p className='text-amber-100 text-sm'>{selectedVideo.UserName || 'Unknown User'}</p>
                </div>
                <button onClick={() => { setSelectedVideo(null); setSelectedAction(''); }} className='text-white hover:text-gray-200'>
                  <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                  </svg>
                </button>
              </div>
            </div>
            <div className='p-6 space-y-4'>
              {/* Video Status Info */}
              {selectedVideo.VideoStatus && (
                <div className='flex items-center gap-2'>
                  <span className='text-sm text-gray-600'>Current Status:</span>
                  <span className={`px-2 py-1 rounded text-xs font-bold ${
                    selectedVideo.VideoStatus === 'review_required' ? 'bg-purple-100 text-purple-700' :
                    selectedVideo.VideoStatus === 'under_review' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedVideo.VideoStatus.replace(/_/g, ' ').toUpperCase()}
                  </span>
                </div>
              )}

              {/* Existing Reason */}
              {selectedVideo.VideoStatusReason && (
                <div className='bg-amber-50 border border-amber-200 rounded-lg p-3'>
                  <p className='text-xs text-amber-600 font-medium mb-1'>Previous Reason:</p>
                  <p className='text-sm text-amber-800'>{selectedVideo.VideoStatusReason}</p>
                </div>
              )}

              {/* Every removal reason, one click each.
                  Generated from REASONS so this list cannot drift from the vocabulary the
                  API actually stores, and grouped by consequence: ZERO closes the account,
                  SEVERE and STANDARD record a violation, HOUSEKEEPING records none. */}
              {REASON_GROUPS.map((group) => (
                <div key={group.title}>
                  <div className='flex items-baseline gap-2 mb-1.5'>
                    <span className={'text-[10px] font-bold uppercase tracking-wider ' + group.labelClass}>
                      {group.title}
                    </span>
                    <span className='text-[10px] text-gray-400'>{group.note}</span>
                  </div>
                  <div className='grid grid-cols-2 gap-2'>
                    {group.codes.map((code) => {
                      const r = REASONS.find((x) => x.code === code);
                      if (!r) return null;
                      return (
                        <button
                          key={code}
                          onClick={() => {
                            if (group.confirm && !window.confirm(r.label + '\n\nZero tolerance: this removes the video, records a permanent violation and closes the account.\n\nContinue?')) return;
                            confirmAction('blocked', r.value);
                          }}
                          disabled={actionLoading}
                          title={r.value}
                          className={'px-3 py-2 text-xs font-semibold rounded-lg border transition-all disabled:opacity-50 text-left ' + group.btnClass}
                        >
                          {r.code.replace(/_/g, ' ')}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className='flex justify-end gap-3 pt-2'>
                <button
                  onClick={() => { setSelectedVideo(null); setSelectedAction(''); setReasonInput(''); }}
                  className='px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-all'
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmAction('blocked', reasonInput)}
                  disabled={!reasonInput.trim() || actionLoading}
                  className='px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md flex items-center gap-2'
                >
                  {actionLoading ? (
                    <>
                      <svg className='animate-spin h-4 w-4' fill='none' viewBox='0 0 24 24'>
                        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                        <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                      </svg>
                      Blocking...
                    </>
                  ) : (
                    <>
                      <span>🚫</span>
                      Block Video
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default BulkReview;
